import {
  TRANSACTION_HISTORY_MODULES as MODULES,
  ACTIVITY_DOWNLOAD_MODULES as ACTIVITY_MODULES
} from '../../../constants/commonConstants';

/**
 * FIN-13571 — single source of truth for where an async-download notification leads.
 *
 * The bell dropdown (`dropdown.js`) and the SSE toast (`NotificationListener.js`) each carried
 * their own copy of this routing. The copies drifted: the toast never gained the FIN-12835
 * activity branch, the FIN-12986 multi-alert guard or the FIN-12985 customer fallback, so
 * clicking an activity toast did nothing. Both callers now resolve the target here and only
 * differ in how they navigate.
 *
 * @param {Object} props - the notification's additional properties / the SSE event payload
 * @returns {{ targetPath: string, search: string } | null} null when there is no target
 *   (e.g. a queue-wide export) — callers must not guess a route.
 */
export const resolveNotificationTarget = ({
  module,
  downloadType,
  alertId,
  partyId,
  caseId,
  customerId
} = {}) => {
  const customerKey = partyId || customerId;

  // FIN-12986 — an alertId carrying a comma is a customer-scoped MULTI-alert export, not one
  // alert. Interpolated into a path segment it produced
  // `/app/trm/run-instance/FS...0274,FS...1857` and a 404.
  //
  // The backend no longer writes a multi-alert scope to the job's alert_id, but this guard
  // stays for two reasons: notifications for jobs created before that fix still carry the old
  // value, and it keeps the route-building here honest about what the field means. Treating it
  // as "no single alert" makes those clicks fall through to the customer-scoped target below,
  // which is where a customer-scoped export belongs.
  const singleAlertId =
    alertId && !String(alertId).includes(',') ? alertId : null;

  // FIN-12835 — Activity exports persist under TM_ACTIVITY / CM_TM_ACTIVITY /
  // CM_CASE_TM_ACTIVITY / CM_MANUAL_CUSTOMER_ACTIVITY, none of which contain '_history'.
  if (Object.values(ACTIVITY_MODULES).includes(module)) {
    // FIN-13619 — alert/case-scoped activity exports land on Download History, like the sibling
    // transaction export (FIN-12835 originally sent them to tab 3, Activity Information).

    // FIN-13392 — FRAML customer-detail Activity Events export is customer-scoped by definition:
    // checked first so a stray caseId/alertId can't pull it onto a case route.
    if (module === ACTIVITY_MODULES.FRAML_CUSTOMER_ACTIVITY) {
      return customerKey
        ? {
            targetPath: `/app/cm/framl/customer/${customerKey}`,
            search: '?tab=activity-details'
          }
        : null;
    }
    // Keyed on a customer within a case, not an alert. Checked before the generic caseId
    // branch, which used to catch it and build `?alertId=undefined`; without a caseId the
    // route would be `/cases-by-department/undefined`, so there is no target.
    if (module === ACTIVITY_MODULES.CM_MANUAL_CUSTOMER_ACTIVITY) {
      return caseId && customerKey
        ? {
            targetPath: `/app/cm/cases-by-department/${caseId}`,
            search: `?customerId=${customerKey}&tab=downloadHistory`
          }
        : null;
    }
    // A comma-joined alertId next to a caseId is a customer scope (FIN-12986): falls through.
    if (caseId && singleAlertId) {
      return {
        targetPath: `/app/cm/cases-by-department/${caseId}`,
        search: `?alertId=${singleAlertId}&tab=downloadHistory`
      };
    }
    if (singleAlertId) {
      // CM_CASE_TM_ACTIVITY is here too: the activity export request carries no caseId
      // (ActivityInformation sends customerId/alertId only), so the case branch above can't
      // fire for it and it would otherwise fall through to the FRAML customer page.
      if (
        module === ACTIVITY_MODULES.CM_TM_ACTIVITY ||
        module === ACTIVITY_MODULES.CM_CASE_TM_ACTIVITY
      ) {
        return {
          targetPath: `/app/cm/alerts/${singleAlertId}`,
          search: '?tab=downloadHistory'
        };
      }
      if (module === ACTIVITY_MODULES.TM_ACTIVITY) {
        return {
          targetPath: `/app/trm/run-instance/${singleAlertId}`,
          search: '?tab=downloadHistory'
        };
      }
    }

    // FIN-12985 — customer-scoped activity export: no caseId and no single alertId. These jobs
    // persist `partyId` (verified on keycloak-qa). Lands on the customer's ACTIVITY DETAILS tab,
    // which is what FIN-12985 asks for — NOT Download History: the point of the notification is
    // to land on the activity it was produced from. The transaction branch below keeps
    // transaction-download-history, because that IS what FIN-12936 specified for it.
    if (customerKey) {
      return {
        targetPath: `/app/cm/framl/customer/${customerKey}`,
        search: '?tab=activity-details'
      };
    }

    // Genuinely no target (e.g. a queue-wide export): do nothing rather than guess a route.
    return null;
  }

  const isHistoryNotif = module?.toLowerCase()?.includes('_history');
  if (!isHistoryNotif && downloadType !== 'case') return null;

  // Case-type download (e.g., case transaction history)
  if (downloadType === 'case') {
    return {
      targetPath: `/app/cm/cases-by-department/${caseId}/take-action`,
      search: '?downloadHistory=true'
    };
  }

  // CM case transaction history (no specific TM/CRS subtype)
  if (module === MODULES.CM_CASE_TRANSACTION_HISTORY && caseId) {
    return {
      targetPath: `/app/cm/cases-by-department/${caseId}`,
      search: '?downloadHistory=true'
    };
  }

  // CM case-level TM/CRS/FRAML transaction history — case page, alert tab, download history
  if (
    (module === MODULES.CM_CASE_TM_TRANSACTION_HISTORY ||
      module === MODULES.CM_CASE_CRS_TRANSACTION_HISTORY ||
      module === MODULES.CM_CASE_FRAML_ALERT_TRANSACTION_HISTORY) &&
    caseId
  ) {
    return {
      targetPath: `/app/cm/cases-by-department/${caseId}`,
      search: `?alertId=${alertId}&tab=downloadHistory`
    };
  }

  // FIN-12936 — FRAML customer-detail export (customer-scoped, no single alert):
  // land on the customer's Download History tab.
  if (module === MODULES.FRAML_CUSTOMER_TRANSACTION_HISTORY) {
    return {
      targetPath: `/app/cm/framl/customer/${customerKey}`,
      search: '?tab=transaction-download-history'
    };
  }

  // Alert-based downloads
  if (singleAlertId) {
    // CM alert transaction history (TM/CRS/FRAML)
    if (
      module === MODULES.CM_TM_TRANSACTION_HISTORY ||
      module === MODULES.CM_CRS_TRANSACTION_HISTORY ||
      module === MODULES.CM_FRAML_ALERT_TRANSACTION_HISTORY
    ) {
      return {
        targetPath: `/app/cm/alerts/${singleAlertId}`,
        search: '?tab=downloadHistory'
      };
    }

    // TRM transaction history (TM + FRAML-Prevention run-instance)
    if (
      module === MODULES.TM_TRANSACTION_HISTORY ||
      module === MODULES.FRAML_ALERT_TRANSACTION_HISTORY
    ) {
      return {
        targetPath: `/app/trm/run-instance/${singleAlertId}`,
        search: '?tab=downloadHistory'
      };
    }

    // CRS transaction history
    if (module === MODULES.CRS_TRANSACTION_HISTORY) {
      return {
        targetPath: `/app/crs/details/${customerKey}`,
        search: '?tab=downloadHistory'
      };
    }

    // CM manual customer/case transaction history
    if (
      module === MODULES.CM_MANUAL_CUSTOMER_TRANSACTION_HISTORY ||
      module === MODULES.CM_MANUAL_CASE_TRANSACTION_HISTORY
    ) {
      return {
        targetPath: `/app/cm/cases-by-department/${caseId}`,
        search: `?customerId=${customerKey}&tab=downloadHistory`
      };
    }
    return null;
  }

  // PartyId-based CRS (no alertId)
  if (partyId && module === MODULES.CRS_TRANSACTION_HISTORY) {
    return {
      targetPath: `/app/crs/details/${customerKey}`,
      search: '?tab=downloadHistory'
    };
  }

  // FIN-12986 / FIN-12985 — customer-scoped transaction export: either it never carried an
  // alertId, or it carried a multi-alert scope that `singleAlertId` correctly rejected above.
  // Both mean "this export belongs to a customer, not an alert", so land on the customer's
  // Download History tab instead of a 404 alert route or a dead click.
  if (customerKey) {
    return {
      targetPath: `/app/cm/framl/customer/${customerKey}`,
      search: '?tab=transaction-download-history'
    };
  }

  return null;
};
